// "Expand to article": the way from the stock composer (right pane of the three-pane layout, or the stock compose
// column) into the long-form editor. Lives in the main bundle (compose_form.jsx renders it), so it must stay small
// and must not import TipTap or anything else from the editor chunk (./prefetch.ts only starts loading it).
import { useCallback, useState } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import type { List as ImmutableList, Map as ImmutableMap } from 'immutable';

import CloseIcon from '@/material-icons/400-24px/close.svg?react';
import DescriptionIcon from '@/material-icons/400-24px/description.svg?react';
import { resetCompose } from 'mastodon/actions/compose';
import { showAlertForError } from 'mastodon/actions/alerts';
import { Icon } from 'mastodon/components/icon';
import { IconButton } from 'mastodon/components/icon_button';
import { useAppHistory } from 'mastodon/components/router';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import { apiCreateArticle } from '../api';
import type { TiptapDoc, TiptapNode } from '../types';

import classes from './compose_entry.module.scss';
import { writePath } from './paths';
import { prefetchOnIntent } from './prefetch';

const messages = defineMessages({
  expand: {
    id: 'whocare_article.editor.expand_to_article',
    defaultMessage: 'Expand to article',
  },
  dismiss: {
    id: 'whocare_article.editor.expand_hint_dismiss',
    defaultMessage: 'Do not show again',
  },
});

/** Compose text longer than this gets a one-time hint that it can become an article. */
const LONG_TEXT = 400;
const HINT_KEY = 'whocare.article.expand_hint';

const readHintDismissed = () => {
  try {
    return window.localStorage.getItem(HINT_KEY) === 'dismissed';
  } catch {
    return false;
  }
};

const writeHintDismissed = () => {
  try {
    window.localStorage.setItem(HINT_KEY, 'dismissed');
  } catch {
    // private mode: the hint simply shows again next time
  }
};

type MediaAttachment = ImmutableMap<string, unknown>;

/**
 * Plain compose text -> article body: blank lines separate paragraphs, single line breaks stay hard breaks.
 * Image attachments of the post come along as image nodes (they already belong to the author).
 */
export const composeToDoc = (
  text: string,
  media: MediaAttachment[] = [],
): TiptapDoc => {
  const content: TiptapNode[] = text
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)
    .map((block) => {
      const lines = block.split('\n');
      const inline: TiptapNode[] = [];

      lines.forEach((line, index) => {
        if (index > 0) {
          inline.push({ type: 'hardBreak' });
        }

        if (line.length > 0) {
          inline.push({ type: 'text', text: line });
        }
      });

      return { type: 'paragraph', content: inline };
    });

  media.forEach((attachment) => {
    const url = attachment.get('url') as string | null;

    if (attachment.get('type') !== 'image' || !url) {
      return;
    }

    const attrs: Record<string, unknown> = {
      mediaId: attachment.get('id'),
      src: url,
    };
    const description = attachment.get('description') as string | null;

    if (description) {
      attrs.alt = description;
    }

    content.push({ type: 'image', attrs });
  });

  return { type: 'doc', content };
};

const useExpand = () => {
  const dispatch = useAppDispatch();
  const history = useAppHistory();
  const [busy, setBusy] = useState(false);
  const compose = useAppSelector((state) => state.compose);

  const expand = useCallback(() => {
    if (busy) {
      return;
    }

    const text = (compose.get('text') as string | undefined) ?? '';
    const media = (
      compose.get('media_attachments') as
        | ImmutableList<MediaAttachment>
        | undefined
    )?.toArray();
    const language = (compose.get('language') as string | null) ?? null;

    if (text.trim().length === 0 && !media?.length) {
      history.push(writePath());
      return;
    }

    setBusy(true);

    apiCreateArticle({ body_json: composeToDoc(text, media), language })
      .then((article) => {
        dispatch(resetCompose());
        history.push(writePath(article.id));
        return article;
      })
      .catch((error: unknown) => {
        dispatch(showAlertForError(error));
      })
      .finally(() => {
        setBusy(false);
      });
  }, [busy, compose, dispatch, history]);

  // Replies, edits and quotes stay posts: an article is always a new top-level status.
  const available =
    compose.get('in_reply_to') == null &&
    compose.get('id') == null &&
    compose.get('quoted_status_id') == null &&
    compose.get('poll') == null;

  return { expand, busy, available };
};

/** Small icon button in the compose form's tool row. */
export const ExpandToArticleButton: React.FC = () => {
  const intl = useIntl();
  const { expand, busy, available } = useExpand();

  if (!available) {
    return null;
  }

  return (
    <IconButton
      ref={prefetchOnIntent}
      className={classes.button}
      icon='file-text'
      iconComponent={DescriptionIcon}
      title={intl.formatMessage(messages.expand)}
      onClick={expand}
      disabled={busy}
      inverted
    />
  );
};

/** One-time hint below the text area once the post gets long. */
export const ExpandToArticleHint: React.FC = () => {
  const intl = useIntl();
  const { expand, busy, available } = useExpand();
  const [dismissed, setDismissed] = useState(readHintDismissed);
  const long = useAppSelector(
    (state) =>
      ((state.compose.get('text') as string | undefined) ?? '').length >
      LONG_TEXT,
  );

  const handleDismiss = useCallback(() => {
    writeHintDismissed();
    setDismissed(true);
  }, []);

  const handleExpand = useCallback(() => {
    writeHintDismissed();
    expand();
  }, [expand]);

  if (!available || dismissed || !long) {
    return null;
  }

  return (
    <div className={classes.hint} role='status'>
      <span className={classes.hintText}>
        <FormattedMessage
          id='whocare_article.editor.expand_hint'
          defaultMessage='This is getting long. Turn it into an article with headings and images?'
        />
      </span>
      <button
        ref={prefetchOnIntent}
        type='button'
        className={classes.hintAction}
        onClick={handleExpand}
        disabled={busy}
      >
        {intl.formatMessage(messages.expand)}
      </button>
      <button
        type='button'
        className={classes.hintClose}
        onClick={handleDismiss}
        title={intl.formatMessage(messages.dismiss)}
        aria-label={intl.formatMessage(messages.dismiss)}
      >
        <Icon id='times' icon={CloseIcon} />
      </button>
    </div>
  );
};
